import injectStores from 'components/RootStore/injectStores';
import {
  action,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';
import React from 'react';
import { Index, MultiGrid as VirtualMultiGrid } from 'react-virtualized';
import RootStore from 'stores/RootStore';
// Use the 'as any' typescript cast technique to bypass strict type checking

export interface MultiGridCellRendererProps {
  columnIndex: number;
  rowIndex: number;
  isScrolling: boolean;
  isVisible: boolean;
  key: string;
  parent: unknown;
  style: React.CSSProperties;
  rootStore?: RootStore;
}

interface MultiGridProps {
  leftHeaderCellRenderer: (
    props: MultiGridCellRendererProps
  ) => React.ReactNode;
  rightHeaderCellRenderer: (
    props: MultiGridCellRendererProps
  ) => React.ReactNode;
  leftBodyCellRenderer: (props: MultiGridCellRendererProps) => React.ReactNode;
  rightBodyCellRenderer: (props: MultiGridCellRendererProps) => React.ReactNode;
  fixedColumnCount: number;
  fixedRowCount: number;
  height: number;
  width: number;
  rowHeight: number | ((params: Index) => number);
  columnWidth: number | ((props: Index) => number);
  data: object[] | null;
  columnKeys: string[];
  scrollToRow?: number;
  scrollToColumn?: number;
  overscanRowCount?: number;
  overscanColumnCount?: number;
  enableFixedRowScroll: boolean;
  enableFixedColumnScroll: boolean;
  enableColumnHeaders?: boolean;
  setRef: (ref: VirtualMultiGrid | null) => void;
  saveLocation?: string;
  rootStore?: RootStore;
}

class MultiGrid extends React.PureComponent<MultiGridProps> {
  scrollbarSize = 15;

  scrollHeight = 0;
  scrollLeft = 0;
  scrollTop = 0;
  scrollWidth = 0;

  scrollLeftDispose: IReactionDisposer | null = null;
  scrollTopDispose: IReactionDisposer | null = null;

  constructor(props: MultiGridProps) {
    super(props);
    makeObservable(this, {
      scrollHeight: observable,
      scrollLeft: observable,
      scrollTop: observable,
      scrollWidth: observable,
      handleScroll: action,
      setValue: action,
      // columnCount: computed,
      // rowCount: computed,
      // rowData: computed,
      // scrollLeftComputed: computed,
      // scrollTopComputed: computed,
    });
    if (props.saveLocation) {
      console.log('saveLocation', props.saveLocation);
      this.checkSaveLocation(props.saveLocation);

      this.scrollLeftDispose = reaction(
        () => this.scrollLeft,
        (left) => {
          console.log('scrollLeft reaction', left);
          localStorage.setItem(
            `scrollLeft-${props.saveLocation}`,
            String(left)
          );
        },
        { delay: 1000 }
      );

      this.scrollTopDispose = reaction(
        () => this.scrollTop,
        (top) => {
          console.log('scrollTop reaction', top);
          localStorage.setItem(`scrollTop-${props.saveLocation}`, String(top));
        },
        { delay: 1000 }
      );
    }
  }

  checkSaveLocation(saveLocation: string | undefined) {
    console.log('checkSaveLocation');
    if (!saveLocation) {
      console.warn(
        'MultiGrid component is missing a saveLocation prop. This is required for saving scroll position.'
      );
    }
    const restoreLeft = localStorage.getItem(`scrollLeft-${saveLocation}`);
    if (restoreLeft) {
      this.setValue('scrollLeft', parseInt(restoreLeft));
    }
    const restoreTop = localStorage.getItem(`scrollTop-${saveLocation}`);
    if (restoreTop) {
      this.setValue('scrollTop', parseInt(restoreTop));
    }
  }

  dispose = () => {
    this.scrollLeftDispose && this.scrollLeftDispose();
    this.scrollTopDispose && this.scrollTopDispose();
  };
  componentWillUnmount() {
    this.dispose();
  }

  setValue = (key: keyof MultiGrid, value: number) => {
    if (key === 'scrollHeight') {
      this.scrollHeight = value;
    }
    if (key === 'scrollLeft') {
      this.scrollLeft = value;
    }
    if (key === 'scrollTop') {
      this.scrollTop = value;
    }
    if (key === 'scrollWidth') {
      this.scrollWidth = value;
    }
    // this[key] = value;
  };

  handleScroll = ({
    scrollHeight,
    scrollLeft,
    scrollTop,
    scrollWidth,
  }: {
    scrollHeight: number;
    scrollLeft: number;
    scrollTop: number;
    scrollWidth: number;
  }) => {
    // console.log('handleScroll');
    // console.log('scrollTop', scrollTop);
    // console.log('this.scrollTop', this.scrollTop);
    if (scrollHeight !== this.scrollHeight) {
      this.setValue('scrollHeight', scrollHeight);
    }
    if (scrollLeft !== this.scrollLeft) {
      this.setValue('scrollLeft', scrollLeft);
    }
    if (scrollTop !== this.scrollTop) {
      this.setValue('scrollTop', scrollTop);
    }
    if (scrollWidth !== this.scrollWidth) {
      this.setValue('scrollWidth', scrollWidth);
    }
  };

  handleCellRender = (cellProps: MultiGridCellRendererProps) => {
    // console.log('handleCellRender');
    const {
      fixedColumnCount,
      fixedRowCount,
      leftHeaderCellRenderer,
      rightHeaderCellRenderer,
      leftBodyCellRenderer,
      rightBodyCellRenderer,
      rootStore,
    } = this.props as MultiGridProps;
    // console.log('handleCellRender rootStore', rootStore);
    const { columnIndex, rowIndex } = cellProps;
    if (columnIndex < fixedColumnCount && rowIndex < fixedRowCount)
      return leftHeaderCellRenderer({ ...cellProps, rootStore });
    if (columnIndex >= fixedColumnCount && rowIndex < fixedRowCount)
      return rightHeaderCellRenderer({ ...cellProps, rootStore });
    if (columnIndex < fixedColumnCount && rowIndex >= fixedRowCount)
      return leftBodyCellRenderer({ ...cellProps, rootStore });
    return rightBodyCellRenderer({ ...cellProps, rootStore });
  };

  get rowData() {
    const data = this.props.data as unknown[];
    if (!data) return [];
    return [...data];
  }

  get rowCount() {
    if (this.props.enableColumnHeaders) {
      return this.rowData.length + 1;
    }
    return this.rowData.length;
  }

  get columnCount() {
    const { columnKeys } = this.props as MultiGridProps;
    return columnKeys.length;
  }
  get scrollLeftComputed() {
    return this.scrollLeft;
  }

  get scrollTopComputed() {
    // console.log('scrollTopComputed');
    return this.scrollTop;
  }

  render() {
    // console.log('MultiGridRender');
    const {
      fixedColumnCount,
      fixedRowCount,
      columnWidth,
      rowHeight,
      height,
      width,
      scrollToRow,
      scrollToColumn,
      overscanRowCount,
      overscanColumnCount,
      enableFixedRowScroll,
      enableFixedColumnScroll, setRef,
    } = this.props as MultiGridProps;
    // console.log("rowCount", this.rowCount);

    // Cast to any to bypass React 18.3 type checking
    const MultiGridComponent = VirtualMultiGrid as any;

    return (
      <MultiGridComponent
        ref={(ref: any) => {
          if (setRef) setRef(ref);
        }}
        cellRenderer={this.handleCellRender}
        fixedColumnCount={fixedColumnCount}
        fixedRowCount={fixedRowCount}
        columnWidth={columnWidth}
        rowHeight={rowHeight}
        height={height}
        width={width}
        scrollToRow={scrollToRow}
        scrollToColumn={scrollToColumn}
        rowCount={this.rowCount}
        columnCount={this.columnCount}
        overscanRowCount={overscanRowCount}
        overscanColumnCount={overscanColumnCount}
        scrollTop={this.scrollTopComputed}
        scrollLeft={this.scrollLeftComputed}
        onScroll={this.handleScroll}
        enableFixedRowScroll={enableFixedRowScroll}
        enableFixedColumnScroll={enableFixedColumnScroll}
        hideTopRightGridScrollbar={enableFixedRowScroll}
        hideBottomLeftGridScrollbar={enableFixedColumnScroll}
      />
    );
  }
}

const MultiGridComponent = injectStores(MultiGrid);

export default MultiGridComponent;
